import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * useDemoRunner — orchestrator for the DEMO "Aprender a Jogar" tutorial.
 *
 *  • Loads guide phrases from `/api/demo/phrases` on mount and picks the
 *    text in the active `lang`.
 *  • Exposes helpers `showPhrase(key)` and `hidePhrase()` used by the
 *    GamePlay demo state-machine to display the right guide at each step.
 *  • Provides `getScriptedAward(currentProgress)` — a deterministic mirror
 *    of `localSupertAward` that forces the sequence:
 *        S → U → P → E → P (repeat) → ★ → R  (7 rounds, completes SUPERT)
 *    so the tutorial finishes fast AND shows all three UI states the
 *    player will ever see: unique letter, repeated letter, star wildcard.
 *
 * The hook does NOT own any timers — GamePlay drives the pacing so it can
 * synchronize with the roulette spin, card animations and confetti bursts.
 */
const SCRIPT = ["S", "U", "P", "E", "P", "*", "R"];

export function useDemoRunner({ enabled, lang }) {
  const [phrasesByKey, setPhrasesByKey] = useState({});
  const [current, setCurrent] = useState(null);
  const seqRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      // Clear cached phrases when DEMO exits so the next run picks up any
      // backoffice edits (position offsets, text, duration, etc.) made
      // while the user was outside the tutorial — no stale data between
      // runs.
      setPhrasesByKey({});
      return;
    }
    let cancelled = false;
    // Cache-bust so proxy/browser HTTP cache can't serve stale offsets.
    api.get(`/demo/phrases?_t=${Date.now()}`).then(({ data }) => {
      if (cancelled) return;
      const map = {};
      for (const p of data || []) {
        map[p.key] = p;
      }
      setPhrasesByKey(map);
    }).catch((err) => {
      console.error("[demo/phrases]", err);
    });
    return () => { cancelled = true; };
  }, [enabled]);

  const pickText = useCallback(
    (p) => {
      if (!p) return "";
      const l = (lang || "pt").toLowerCase();
      return p[`text_${l}`] || p.text_pt || "";
    },
    [lang],
  );

  const showPhrase = useCallback(
    (key) => {
      const p = phrasesByKey[key];
      if (!p || p.enabled === false) return;
      seqRef.current += 1;
      setCurrent({
        __id: `${key}-${seqRef.current}`,
        key,
        text: pickText(p),
        color: p.color,
        font_size: p.font_size,
        offset_x: Number(p.offset_x) || 0,
        offset_y: Number(p.offset_y) || 0,
        effect: p.effect || "fade",
        duration_ms: p.duration_ms || 2200,
      });
    },
    [phrasesByKey, pickText],
  );

  const hidePhrase = useCallback(() => setCurrent(null), []);

  /**
   * Returns the scripted award for a given round.
   * If the scripted item was a repeat, `progress` stays unchanged.
   */
  const getScriptedAward = useCallback((roundIdx, prevProgress) => {
    const item = SCRIPT[Math.min(roundIdx, SCRIPT.length - 1)];
    const isRepeat = item !== "*" && prevProgress.includes(item);
    const nextProgress = isRepeat ? [...prevProgress] : [...prevProgress, item];
    return { item, progress: nextProgress, was_repeat: isRepeat };
  }, []);

  return {
    phrasesByKey,
    currentPhrase: current,
    showPhrase,
    hidePhrase,
    getScriptedAward,
    scriptLength: SCRIPT.length,
  };
}
